/**
 * The account refresh — `GET /api/me` and the one place that knows what a 401 means for the
 * session. §11 makes that route the only request a not-approved account may make, so both the
 * waiting screen's poll and the socket's 4403 handler come through here.
 *
 * Lives apart from `lib/data.ts` because the socket needs it and `data.ts` already imports the
 * socket; this module depends on the api client and the stores only, so there is no cycle.
 */
import { ApiClientError } from '@olmt/shared'
import { api } from './api'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { useSearchStore } from '../stores/searchStore'

/**
 * Re-reads the account and stores it. Returns false when it could not be read — a network blip, or
 * a revoked device token, which is the one answer that ends the session (a status change does not:
 * the waiting screen is where a non-approved account belongs).
 */
export async function refreshAccount(): Promise<boolean> {
  const token = useAuthStore.getState().token
  if (!token) return false
  try {
    const { user, privacy } = await api.me()
    // A lookup belongs to the session that started it; a sign-out drops its result.
    if (useAuthStore.getState().token !== token) return false
    useAuthStore.getState().setUser(user)
    useAuthStore.getState().setPrivacy(privacy)
    return true
  } catch (error) {
    if (error instanceof ApiClientError && error.code === 'unauthorized') {
      useAuthStore.getState().clearSession()
      useChatStore.getState().reset()
      useSearchStore.getState().clear()
    }
    return false
  }
}
